export type BrowserAsrState = 'idle' | 'connecting' | 'recording' | 'finishing';

export interface BrowserAsrSessionCallbacks {
  onStateChange?: (state: BrowserAsrState) => void;
  onPartial?: (text: string) => void;
  onFinal?: (text: string) => void;
  onCompleted?: () => void;
  onError?: (error: Error) => void;
}

interface AsrServerMessage {
  type?: 'ready' | 'partial' | 'final' | 'completed' | 'error';
  text?: string;
  code?: string;
  message?: string;
}

const TARGET_SAMPLE_RATE = 16_000;
const READY_TIMEOUT_MS = 15_000;
const AUDIO_WORKLET_NAME = 'boclaw-pcm-capture';
const AUDIO_WORKLET_SOURCE = `
class BoclawPcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs, outputs) {
    const input = inputs[0] && inputs[0][0];
    if (input && input.length > 0) {
      const copy = input.slice();
      this.port.postMessage(copy.buffer, [copy.buffer]);
    }
    const output = outputs[0] && outputs[0][0];
    if (output) output.fill(0);
    return true;
  }
}
registerProcessor('${AUDIO_WORKLET_NAME}', BoclawPcmCaptureProcessor);
`;

function joinRecognizedText(parts: string[]): string {
  return parts.map((part) => part.trim()).filter(Boolean).join('');
}

/** 将录音前文本、已确认分段和当前增量预览组合为输入框内容。 */
export function composeAsrText(baseText: string, finalSegments: string[], partialText: string): string {
  const recognizedText = joinRecognizedText([...finalSegments, partialText]);
  if (!recognizedText) return baseText;
  if (!baseText) return recognizedText;
  if (/\s$/.test(baseText)) return `${baseText}${recognizedText}`;
  return `${baseText}\n${recognizedText}`;
}

/**
 * 保留跨音频块的重采样位置，避免逐块取整导致时长漂移。
 * 输出固定为 FunASR 所需的 16-bit little-endian PCM。
 */
export class StreamingPcm16Encoder {
  private pending = new Float32Array(0);
  private readPosition = 0;
  private readonly step: number;

  constructor(sourceSampleRate: number, targetSampleRate = TARGET_SAMPLE_RATE) {
    if (!Number.isFinite(sourceSampleRate) || sourceSampleRate <= 0) {
      throw new Error('无效的音频采样率');
    }
    if (!Number.isFinite(targetSampleRate) || targetSampleRate <= 0) {
      throw new Error('无效的目标采样率');
    }
    this.step = sourceSampleRate / targetSampleRate;
  }

  encode(input: Float32Array): ArrayBuffer {
    if (input.length === 0) return new ArrayBuffer(0);

    const samples = new Float32Array(this.pending.length + input.length);
    samples.set(this.pending);
    samples.set(input, this.pending.length);

    const output: number[] = [];
    while (this.readPosition + 1 < samples.length) {
      const leftIndex = Math.floor(this.readPosition);
      const rightIndex = leftIndex + 1;
      const ratio = this.readPosition - leftIndex;
      const sample = samples[leftIndex] + (samples[rightIndex] - samples[leftIndex]) * ratio;
      output.push(Math.max(-1, Math.min(1, sample)));
      this.readPosition += this.step;
    }

    const consumed = Math.floor(this.readPosition);
    this.pending = samples.slice(Math.min(consumed, samples.length));
    this.readPosition -= consumed;

    const buffer = new ArrayBuffer(output.length * 2);
    const view = new DataView(buffer);
    output.forEach((sample, index) => {
      const value = sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff);
      view.setInt16(index * 2, value, true);
    });
    return buffer;
  }
}

function buildAsrWebSocketUrl(): string {
  const token = localStorage.getItem('boclaw_token');
  const userId = localStorage.getItem('boclaw_user_id');
  if (!token) throw new Error('登录状态已失效，请重新登录');

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({ token });
  if (userId) params.set('userId', userId);
  return `${protocol}//${window.location.host}/ws/asr?${params.toString()}`;
}

function normalizeBrowserAsrError(error: unknown): Error {
  if (error instanceof DOMException && error.name === 'NotAllowedError') {
    return new Error('未获得麦克风权限，请在浏览器设置中允许访问麦克风');
  }
  if (error instanceof DOMException && error.name === 'NotFoundError') {
    return new Error('未检测到可用的麦克风');
  }
  return error instanceof Error ? error : new Error('语音识别启动失败');
}

export class BrowserAsrSession {
  private state: BrowserAsrState = 'idle';
  private ws: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private captureNode: AudioWorkletNode | ScriptProcessorNode | null = null;
  private captureKind: 'worklet' | 'script' | null = null;
  private silentGain: GainNode | null = null;
  private workletUrl: string | null = null;
  private encoder: StreamingPcm16Encoder | null = null;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;
  private readyResolve: (() => void) | null = null;
  private readyReject: ((error: Error) => void) | null = null;
  private cancelled = false;
  private completed = false;
  private failed = false;

  constructor(private readonly callbacks: BrowserAsrSessionCallbacks = {}) {}

  async start(): Promise<void> {
    if (this.state !== 'idle') throw new Error('语音识别会话已经启动');
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('当前浏览器不支持麦克风录音，请使用最新版 Chrome 或 Edge');
    }

    this.setState('connecting');
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (this.cancelled) {
        this.releaseResources(false);
        return;
      }

      await this.connectWebSocket();
      if (this.cancelled) {
        this.releaseResources(false);
        return;
      }

      await this.startAudioCapture();
      if (this.cancelled) {
        this.releaseResources(false);
        return;
      }
      this.setState('recording');
    } catch (error) {
      const normalized = normalizeBrowserAsrError(error);
      this.fail(normalized);
      throw normalized;
    }
  }

  finish(): void {
    if (this.state !== 'recording' || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.setState('finishing');
    this.stopAudioCapture();
    this.ws.send(JSON.stringify({ type: 'finish' }));
  }

  cancel(): void {
    if (this.cancelled || this.completed) return;
    this.cancelled = true;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'cancel' }));
    }
    this.rejectReady(new Error('语音识别已取消'));
    this.releaseResources(true);
    this.setState('idle');
  }

  private connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
      this.readyTimer = setTimeout(() => {
        this.rejectReady(new Error('连接语音识别服务超时'));
      }, READY_TIMEOUT_MS);

      const ws = new WebSocket(buildAsrWebSocketUrl());
      this.ws = ws;
      ws.binaryType = 'arraybuffer';
      ws.onmessage = (event) => this.handleServerMessage(event.data);
      ws.onerror = () => {
        this.rejectReady(new Error('无法连接语音识别服务'));
      };
      ws.onclose = () => {
        if (!this.cancelled && !this.completed && !this.failed) {
          const error = new Error(
            this.state === 'connecting' ? '无法连接语音识别服务' : '语音识别连接已断开',
          );
          if (this.state === 'connecting') this.rejectReady(error);
          else this.fail(error);
        }
      };
    });
  }

  private handleServerMessage(raw: unknown): void {
    let message: AsrServerMessage;
    try {
      const text = typeof raw === 'string' ? raw : String(raw);
      message = JSON.parse(text) as AsrServerMessage;
    } catch {
      this.fail(new Error('语音识别服务返回了无法解析的数据'));
      return;
    }

    switch (message.type) {
      case 'ready':
        this.resolveReady();
        break;
      case 'partial':
        this.callbacks.onPartial?.(message.text || '');
        break;
      case 'final':
        if (message.text?.trim()) this.callbacks.onFinal?.(message.text);
        break;
      case 'completed':
        this.completed = true;
        this.callbacks.onCompleted?.();
        this.releaseResources(true);
        this.setState('idle');
        break;
      case 'error':
        this.fail(new Error(message.message || message.code || '语音识别失败'));
        break;
      default:
        break;
    }
  }

  private async startAudioCapture(): Promise<void> {
    if (!this.stream) throw new Error('麦克风尚未就绪');
    const AudioContextConstructor = window.AudioContext
      || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) throw new Error('当前浏览器不支持 Web Audio');

    const context = new AudioContextConstructor();
    this.audioContext = context;
    if (context.state === 'suspended') await context.resume();
    this.encoder = new StreamingPcm16Encoder(context.sampleRate, TARGET_SAMPLE_RATE);
    this.sourceNode = context.createMediaStreamSource(this.stream);
    this.silentGain = context.createGain();
    this.silentGain.gain.value = 0;
    this.silentGain.connect(context.destination);

    const onSamples = (samples: Float32Array): void => {
      if (this.state === 'finishing' || this.cancelled || this.completed) return;
      const pcm = this.encoder?.encode(samples);
      if (pcm && pcm.byteLength > 0 && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(pcm);
      }
    };

    try {
      if (!context.audioWorklet) throw new Error('AudioWorklet unavailable');
      const blob = new Blob([AUDIO_WORKLET_SOURCE], { type: 'text/javascript' });
      this.workletUrl = URL.createObjectURL(blob);
      await context.audioWorklet.addModule(this.workletUrl);
      const worklet = new AudioWorkletNode(context, AUDIO_WORKLET_NAME, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        onSamples(new Float32Array(event.data));
      };
      this.captureNode = worklet;
      this.captureKind = 'worklet';
    } catch (error) {
      console.warn('[ASR] AudioWorklet unavailable, falling back to ScriptProcessor:', error);
      const processor = context.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (event) => {
        onSamples(event.inputBuffer.getChannelData(0));
      };
      this.captureNode = processor;
      this.captureKind = 'script';
    }

    this.sourceNode.connect(this.captureNode);
    this.captureNode.connect(this.silentGain);
  }

  private stopAudioCapture(): void {
    try { this.sourceNode?.disconnect(); } catch { /* ignore */ }
    try { this.captureNode?.disconnect(); } catch { /* ignore */ }
    try { this.silentGain?.disconnect(); } catch { /* ignore */ }
    if (this.captureKind === 'worklet' && this.captureNode) {
      (this.captureNode as AudioWorkletNode).port.onmessage = null;
    }
    if (this.captureKind === 'script' && this.captureNode) {
      (this.captureNode as ScriptProcessorNode).onaudioprocess = null;
    }
    this.captureNode = null;
    this.captureKind = null;
    this.sourceNode = null;
    this.silentGain = null;
    this.encoder = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    if (this.audioContext) void this.audioContext.close().catch(() => undefined);
    this.audioContext = null;
    if (this.workletUrl) URL.revokeObjectURL(this.workletUrl);
    this.workletUrl = null;
  }

  private resolveReady(): void {
    if (!this.readyResolve) return;
    if (this.readyTimer) clearTimeout(this.readyTimer);
    this.readyTimer = null;
    const resolve = this.readyResolve;
    this.readyResolve = null;
    this.readyReject = null;
    resolve();
  }

  private rejectReady(error: Error): void {
    if (this.readyTimer) clearTimeout(this.readyTimer);
    this.readyTimer = null;
    const reject = this.readyReject;
    this.readyResolve = null;
    this.readyReject = null;
    reject?.(error);
  }

  private fail(error: Error): void {
    if (this.failed || this.cancelled || this.completed) return;
    this.failed = true;
    this.rejectReady(error);
    this.callbacks.onError?.(error);
    this.releaseResources(true);
    this.setState('idle');
  }

  private releaseResources(closeSocket: boolean): void {
    this.stopAudioCapture();
    if (this.readyTimer) clearTimeout(this.readyTimer);
    this.readyTimer = null;
    if (closeSocket && this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, this.completed ? 'completed' : 'cancelled');
      }
    }
    this.ws = null;
  }

  private setState(state: BrowserAsrState): void {
    this.state = state;
    this.callbacks.onStateChange?.(state);
  }
}
